import React, { useEffect, useState } from "react";
import { Loader2, Share2 } from "lucide-react";
import { isNativePlatform } from "@/lib/platform";

const API = process.env.REACT_APP_BACKEND_URL;

const errorMessage = (status) => {
  if (status === 404) return "This document's file couldn't be found.";
  if (status === 401 || status === 403) return "You don't have access to this document.";
  return "We couldn't open this document. Please try again.";
};

const kindOf = (type, name) => {
  const n = name || "";
  if (type === "application/pdf" || /\.pdf$/i.test(n)) return "pdf";
  if (type === "text/html" || /\.html?$/i.test(n)) return "html";
  if (/^image\/(png|jpe?g|gif|webp|heic|heif)$/.test(type)) return "image";
  if (/^text\/plain$|^message\/rfc822$/.test(type) || /\.(eml|txt)$/i.test(n)) return "text";
  return "other";
};

// Blocks scripts, network loads (remote images/trackers), forms and navigation
// inside the opaque-origin sandbox used for uploaded HTML.
const HTML_CSP = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'">`;

// The API sends X-Frame-Options: DENY, so its URL can't be framed. Load the bytes
// with the signed-in session (cookie on web, bearer on native) and show a local copy.
export function useDocumentOriginal(path, name) {
  const [state, setState] = useState({ status: "loading" });
  useEffect(() => {
    if (!path) return undefined;
    let cancelled = false;
    let url = null;
    setState({ status: "loading" });
    (async () => {
      try {
        const res = await fetch(`${API}${path}`, { credentials: "include" });
        if (!res.ok) throw Object.assign(new Error("load failed"), { status: res.status });
        const blob = await res.blob();
        const type = (res.headers.get("content-type") || blob.type || "").split(";")[0].trim().toLowerCase();
        const kind = kindOf(type, name);
        const text = kind === "html" || kind === "text" ? await blob.text() : null;
        // Only inert types get a navigable blob URL; PDF is re-typed so it can't be sniffed as HTML.
        if (kind === "pdf") url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        if (kind === "image") url = URL.createObjectURL(new Blob([blob], { type }));
        if (!cancelled) setState({ status: "ready", kind, url, text, blob, name: name || "document" });
      } catch (e) {
        if (!cancelled) setState({ status: "error", message: errorMessage(e?.status) });
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path, name]);
  return state;
}

// Fallback when the inline preview is blank/unsupported: share sheet where the runtime
// offers file sharing, otherwise (or if sharing fails) a forced download. Bytes are re-typed
// as octet-stream so they never render as app content. AbortError = user dismissed.
// Capacitor iOS 7 has no WKDownload handling, so native never pretends a download happened.
export async function saveDocumentCopy(original) {
  const file = new File([original.blob], original.name, { type: "application/octet-stream" });
  let shareFailed = false;
  if (typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: original.name });
      return { status: "shared" };
    } catch (e) {
      if (e?.name === "AbortError") return { status: "cancelled" };
      shareFailed = true;
    }
  }
  if (isNativePlatform()) {
    if (!shareFailed) throw Object.assign(new Error("sharing isn't available on this device"), { code: "unsupported" });
    throw new Error("sharing failed on this device");
  }
  const url = URL.createObjectURL(file);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = original.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }
  return { status: "downloaded", shareFailed };
}

function SaveCopy({ original, label }) {
  const [note, setNote] = useState(null);
  const onSave = async () => {
    setNote(null);
    try {
      const r = await saveDocumentCopy(original);
      if (r.shareFailed) setNote({ kind: "info", text: "Sharing didn't work here, so a download was started instead." });
    } catch (e) {
      setNote({
        kind: "error",
        text: e?.code === "unsupported"
          ? "Saving a copy isn't available on this device (sharing isn't available on this device)."
          : `We couldn't save a copy${e?.message ? ` (${e.message})` : ""}. Please try again.`,
      });
    }
  };
  return (
    <div className="mt-2 text-xs text-slate-500">
      {note && (
        <p data-testid={`cc-document-original-save-${note.kind}`} className={`mb-1 ${note.kind === "error" ? "text-rose-700" : "text-slate-600"}`}>
          {note.text}
        </p>
      )}
      <div className="flex items-center justify-between gap-3">
        <span>{label}</span>
        <button
          type="button"
          data-testid="cc-document-original-save"
          onClick={onSave}
          className="inline-flex items-center gap-1 min-h-[36px] px-3 rounded-md border border-slate-300 font-semibold text-slate-700 transition-colors hover:bg-slate-50 active:bg-slate-100"
        >
          <Share2 className="w-3.5 h-3.5" /> Save or share a copy
        </button>
      </div>
    </div>
  );
}

function Preview({ original, className }) {
  if (original.kind === "pdf") {
    return <iframe src={original.url} title="Original document" className={className} data-testid="cc-document-iframe" />;
  }
  if (original.kind === "html") {
    return (
      <iframe srcDoc={HTML_CSP + original.text} sandbox="" referrerPolicy="no-referrer"
        title="Original document" className={className} data-testid="cc-document-iframe" />
    );
  }
  if (original.kind === "image") {
    return <img src={original.url} alt="Original document" className="max-w-full h-auto mx-auto" data-testid="cc-document-original-image" />;
  }
  if (original.kind === "text") {
    return (
      <pre data-testid="cc-document-original-text" className={`whitespace-pre-wrap break-words text-xs text-slate-700 overflow-auto ${className}`}>
        {original.text}
      </pre>
    );
  }
  return (
    <div data-testid="cc-document-original-unsupported" className="text-sm p-3 rounded-md bg-slate-50 text-slate-600">
      A preview isn't available for this file type.
    </div>
  );
}

export function DocumentOriginalFrame({ original, className = "" }) {
  if (original.status === "loading") {
    return (
      <div data-testid="cc-document-original-loading" className="flex items-center justify-center gap-2 text-sm text-slate-500 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Opening document…
      </div>
    );
  }
  if (original.status === "error") {
    return (
      <div data-testid="cc-document-original-error" className="text-sm p-3 rounded-md bg-rose-50 text-rose-700">
        {original.message}
      </div>
    );
  }
  return (
    <>
      <Preview original={original} className={className} />
      <SaveCopy original={original} label={original.kind === "other" ? "" : "Blank or hard to read?"} />
    </>
  );
}
